import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, Users, RefreshCw } from "lucide-react";

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

const relTime = (iso) => {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch (_) { return "—"; }
};

export default function RecentSignups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/operations");
      setData({ rows: res.data.recentSignups || [] });
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="recent-signups-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading recent signups…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="recent-signups">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Recent Signups</h3>
          <span className="text-xs text-slate-500">{data.rows.length} latest</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="recent-signups-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="recent-signups-empty">No signups yet across the fleet.</div>
      ) : (
        <div className="space-y-1.5" data-testid="recent-signups-list">
          {data.rows.slice(0, 6).map((r) => (
            <div key={r.id} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`recent-signup-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 font-medium truncate">{r.organisation}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.customerName} · {r.type?.replace(/_/g, " ")}</div>
              </div>
              <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] border px-1.5 py-0">{fmtGBP(r.amountGBP)}</Badge>
              <span className="text-[10px] text-slate-400 w-14 text-right">{relTime(r.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
